import {
  Box,
  Divider,
  Grid,
  makeStyles,
  Menu,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import BlockIcon from '@mui/icons-material/Block'
import React, { useMemo } from 'react'
import {
  Datagrid,
  Filter,
  FunctionField,
  ListProps,
  Pagination,
  ReferenceField,
  sanitizeFetchType,
  SelectInput,
  TextField,
  TextInput,
  useQuery,
  useTranslate,
} from 'react-admin'
import { Authority } from '../../../core/auth/Authority'
import { ResourceName } from '../../../core/ResourceName'
import Button from '../../common/customized-mui-components/Button'
import List from '../../common/customized-ra-components/List'
import FilteredReferenceInput from '../../common/FilteredReferenceInput'
import FilterProps from '../../common/FilterProps'
import QuickFilter from '../../common/QuickFilter'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import {
  CheckIfAnyModuleHasError,
  CheckLastRefreshError,
  DeviceStatusesPreview,
} from '../common/modules/ModuleStatus'
import { EngineAttachEventRecurringBulkButton } from './engine-list/EngineAttachEventRecurringBulkButton'
import { EngineDetachEventRecurringBulkButton } from './engine-list/EngineDetachEventRecurringBulkButton'
import { EngineLockBulkButton } from './engine-list/EngineLockBulkButton'
import { EngineStandbyModeBulkButton } from './engine-list/EngineStandbyModeBulkButton'
import { EngineUnlockBulkButton } from './engine-list/EngineUnlockBulkButton'
import { EngineStandbyModeCompactButton } from './engine-list/EngineStandbyModeCompactButton'
import { DeviceCategoryExtendedDto } from '../../../core/dto/device-category/device-category-extended.dto'
import { DeviceTypes } from '../../../core/enum/DeviceTypes'
import { HardwareVersions } from '../../../core/enum/HardwareVersions'
import { EngineUploadPackBulkButton } from './engine-list/EngineUploadPackBulkButton'
import { DeviceResetModulesBulkButton } from '../../common/DeviceResetModulesBulkButton'
import { DeviceStatusCheck } from '../../../core/enum/DeviceStatusCheck'
import { useColorPalette } from '../../../core/devices/devices.utils'

const useStyles = makeStyles((theme) => ({
  compactStatus: {
    display: 'flex',
    gap: theme.spacing(0.5),
    height: 34,
    cursor: 'default',
  },
}))

const EngineFilters = (props: FilterProps) => {
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()

  return (
    <Filter {...props}>
      <TextInput source="name" alwaysOn autoFocus />
      {hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
        <FilteredReferenceInput
          label="resources.engines.fields.categoryId"
          source="categoryIdWithDescendants"
          reference={ResourceName.DEVICE_CATEGORIES}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: 'hierarchyString',
            showFilter: true,
          }}
        />
      )}
      {hasAuthority(Authority.VIEW_TS_CANS) && (
        <FilteredReferenceInput
          label="resources.engines.fields.tsCanId"
          source="tsCanId"
          reference={ResourceName.TS_CANS}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            showFilter: true,
          }}
        />
      )}
      <QuickFilter source="eventActive" defaultValue />
      <SelectInput
        source="hardwareVersion"
        choices={Object.entries(HardwareVersions).map(([value]) => ({
          id: value,
          name: `resources.enums.hardwareVersions.${value}`,
        }))}
      />
    </Filter>
  )
}

const EngineGridExpand = ({ ...props }) => {
  const { record: engine, serverDateTime } = props
  const t = useTranslate()
  const hasAuthority = useHasAuthority()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  return (
    <Box padding={1}>
      {smallScreen && engine?.categoryId && (
        <Grid container spacing={1}>
          <Grid item xs={4}>
            {t('resources.palms.fields.categoryId')}
          </Grid>
          <Grid item xs={8} style={{ textAlign: 'center' }}>
            {hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
              <ReferenceField
                source="categoryId"
                reference={ResourceName.DEVICE_CATEGORIES}
                link={false}
                sortBy="name"
              >
                <FunctionField<DeviceCategoryExtendedDto>
                  source="hierarchyString"
                  render={(record?: DeviceCategoryExtendedDto) =>
                    `${record?.id}. ${record?.hierarchyString}`
                  }
                />
              </ReferenceField>
            )}
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
      )}
      {smallScreen &&
        hasAuthority(Authority.VIEW_VERIFIER_DEVICE_DISABLED) &&
        engine?.disabledId && (
          <Grid container spacing={1}>
            <Grid item xs={4}>
              {t('resources.engines.fields.disabled')}
            </Grid>
            <Grid item xs={8} style={{ textAlign: 'center' }}>
              <ReferenceField
                source="disabledId"
                reference={ResourceName.VERIFIER_DEVICE_DISABLED}
                link={false}
                sortBy="name"
                label="resources.engines.fields.disabled"
              >
                <FunctionField
                  source="name"
                  render={(record) => `${record?.id}. ${record?.name}`}
                />
              </ReferenceField>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
        )}
      {smallScreen && engine?.stanbyMode && (
        <Grid container spacing={1}>
          <Grid item xs={4}>
            {t('resources.engines.fields.stanbyMode')}
          </Grid>
          <Grid item xs={8} style={{ textAlign: 'center' }}>
            {t(`resources.enums.standbyMode.${engine?.stanbyMode}`)}
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
      )}
      {engine?.jsonStatus && (
        <Grid item xs={12}>
          <DeviceStatusesPreview
            jsonStatus={engine.jsonStatus}
            deviceType={DeviceTypes.ENGINE}
            referenceDateTime={serverDateTime}
          />
        </Grid>
      )}
    </Box>
  )
}

const EngineBulkActions = ({ ...props }) => {
  const [anchorEl, setAnchorEl] = React.useState(null)

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const EngineLockAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <EngineLockBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const EngineUnlockAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <EngineUnlockBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const EngineLockRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <EngineLockBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          asRecurringJob
        />
      )),
    [props.selectedIds],
  )

  const EngineUnlockRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <EngineUnlockBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          asRecurringJob
        />
      )),
    [props.selectedIds],
  )

  const EngineStandbyModeAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <EngineStandbyModeBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const EngineStandbyModeRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <EngineStandbyModeBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          asRecurringJob
        />
      )),
    [props.selectedIds],
  )

  const EngineAttachEventRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <EngineAttachEventRecurringBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const EngineDetachEventRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <EngineDetachEventRecurringBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const EngineUploadPackAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <EngineUploadPackBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const EngineUploadPackRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <EngineUploadPackBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          asRecurringJob
        />
      )),
    [props.selectedIds],
  )

  const EngineResetModulesAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <DeviceResetModulesBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          resource={ResourceName.ENGINES}
        />
      )),
    [props.selectedIds],
  )

  return (
    <div>
      <Button
        onClick={handleClick}
        label="const.selectAction"
        variant="text"
        color="default"
        useSmallVersionBreakpoint={false}
      />
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
      >
        <EngineLockAction />
        <EngineLockRecurringAction />
        <EngineUnlockAction />
        <EngineUnlockRecurringAction />
        <EngineStandbyModeAction />
        <EngineStandbyModeRecurringAction />
        <EngineAttachEventRecurringAction />
        <EngineDetachEventRecurringAction />
        <EngineResetModulesAction />
        <EngineUploadPackAction />
        <EngineUploadPackRecurringAction />
      </Menu>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CompactStatus = ({ ...props }) => {
  const { record: engine } = props
  const classes = useStyles()

  return (
    <Box
      className={classes.compactStatus}
      onClick={(event) => event.stopPropagation()}
    >
      <EngineStandbyModeCompactButton />
      {engine.disabledId && (
        <Box ml={-7} display="flex" alignItems="center">
          <BlockIcon fontSize="small" />
        </Box>
      )}
    </Box>
  )
}

export const EngineList = ({ ...props }: ListProps) => {
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()
  const colorPalette = useColorPalette(theme)

  const { data: serverDate } = useQuery({
    type: sanitizeFetchType('getServerDateTime'),
    resource: ResourceName.CURRENT_USER,
    payload: {},
  })

  const postRowStyle = (record: any) => {
    if (!serverDate) return {}

    const engineError = CheckLastRefreshError(
      record?.lastRefresh,
      serverDate?.serverDateTime,
    )
    if (engineError) return colorPalette(DeviceStatusCheck.ERROR)

    const engineWarning = CheckIfAnyModuleHasError(
      record.jsonStatus,
      DeviceTypes.ENGINE,
      serverDate?.serverDateTime,
    )
    if (engineWarning) return colorPalette(DeviceStatusCheck.WARNING)
    return colorPalette(DeviceStatusCheck.SUCCESS)
  }

  return (
    <List
      {...props}
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100, 200]} />}
      exporter={false}
      filters={<EngineFilters />}
      bulkActionButtons={<EngineBulkActions />}
    >
      <Datagrid
        rowStyle={postRowStyle}
        size="small"
        rowClick={(id) => `${ResourceName.ENGINES}/${id}/show`}
        isRowExpandable={(record) =>
          (record?.jsonStatus !== undefined && record?.jsonStatus !== null) ||
          (smallScreen && (record?.categoryId || record?.disabledId))
        }
        expand={
          <EngineGridExpand serverDateTime={serverDate?.serverDateTime} />
        }
      >
        <TextField source="id" label="ID" />
        <TextField source="name" />
        {!smallScreen && hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
          <ReferenceField
            source="categoryId"
            reference={ResourceName.DEVICE_CATEGORIES}
            link={false}
            sortBy="name"
          >
            <FunctionField<DeviceCategoryExtendedDto>
              source="hierarchyString"
              render={(record?: DeviceCategoryExtendedDto) =>
                `${record?.id}. ${record?.hierarchyString}`
              }
            />
          </ReferenceField>
        )}
        {!smallScreen && hasAuthority(Authority.VIEW_VERIFIER_DEVICE_DISABLED) && (
          <ReferenceField
            source="disabledId"
            reference={ResourceName.VERIFIER_DEVICE_DISABLED}
            link={false}
            sortBy="name"
            label="resources.engines.fields.disabled"
          >
            <FunctionField
              source="name"
              render={(record) => `${record?.id}. ${record?.name}`}
            />
          </ReferenceField>
        )}
        <CompactStatus source="status" sortable={false} />
      </Datagrid>
    </List>
  )
}
